import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Images, Link2, Loader2, Play, Plus, Trash2, UploadCloud, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { useToast } from '../../ui/Toast';
import { brandKnowledgeApi, MediaCandidate, MediaItem, MediaKind, MediaState } from '../../../services/brandKnowledgeApi';
import { errText } from './pieces';
import { Chip, Panel, SectionHeader } from './ui';

/**
 * The brand's media for planning: files in the Media Library tagged with what they are (product
 * photo, finished ad, video…), the products they show and whether they may run as-is. The
 * strategy's creative approach and each plan brief pick from these, and campaign production uses
 * them. There is one library: every upload or import here lands in the Media Library.
 *
 * mode
 *   onboarding  the optional onboarding step: upload, import from a link or pick from the library,
 *               then tag
 *   summary     Brand Kit: what planning can use, at a glance, with a link to the Media Library
 *   library     the Media Library's "For planning" view: everything tagged, filter, tag, add
 */

export type MediaPanelMode = 'onboarding' | 'summary' | 'library';
export const PLANNING_VIEW = '/media?view=planning';

const RUNNABLE: MediaKind[] = ['finished_ad', 'video'];
const KIND_TONE: Record<MediaKind, 'accent' | 'navy' | 'info' | 'neutral'> = {
  product_photo: 'accent', finished_ad: 'navy', video: 'navy', lifestyle: 'info', logo: 'neutral', other: 'neutral',
};

export const ProductsMediaPanel: React.FC<{ mode?: MediaPanelMode; onChange?: () => void }> = ({ mode = 'library', onChange }) => {
  const navigate = useNavigate();
  const compact = mode === 'onboarding';
  const summary = mode === 'summary';
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [state, setState] = useState<MediaState | null>(null);
  const [filter, setFilter] = useState<MediaKind | 'all'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(0);
  const [picking, setPicking] = useState(false);
  const [linking, setLinking] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try { setState(await brandKnowledgeApi.getMedia()); } catch (e) { toastRef.current.error(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const apply = (next: MediaState) => { setState(next); onChange?.(); };

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    const list = Array.from(files);
    setUploading(list.length);
    let added = 0;
    for (const f of list) {
      try { apply(await brandKnowledgeApi.uploadMedia(f)); added += 1; }
      catch (e) { toastRef.current.error(`${f.name}: ${errText(e)}`); }
      setUploading(n => n - 1);
    }
    if (added) toastRef.current.success(`Added ${added} file${added === 1 ? '' : 's'}. Tag the products they show.`);
    if (fileRef.current) fileRef.current.value = '';
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    (state?.items || []).forEach(i => { c[i.kind] = (c[i.kind] || 0) + 1; });
    return c;
  }, [state]);

  if (!state) return <Panel><div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></Panel>;

  const shown = state.items.filter(i => filter === 'all' || i.kind === filter);
  const productName = (k: string) => state.products.find(p => p.key === k)?.name || k;
  const open = state.items.find(i => i.id === openId) || null;

  const actions = summary ? (
    <>
      <input ref={fileRef} type="file" multiple accept="image/*,video/*" className="hidden" id="media-upload"
        onChange={e => upload(e.target.files)} />
      <Button size="sm" variant="secondary" loading={uploading > 0} onClick={() => fileRef.current?.click()}>
        <UploadCloud className="w-4 h-4 mr-1" />{uploading > 0 ? `Uploading ${uploading}…` : 'Upload'}
      </Button>
      <Button size="sm" variant="primary" onClick={() => navigate(PLANNING_VIEW)}>Manage in Media Library<ArrowRight className="w-4 h-4 ml-1" /></Button>
    </>
  ) : (
    <>
      <input ref={fileRef} type="file" multiple accept="image/*,video/*" className="hidden" id="media-upload"
        onChange={e => upload(e.target.files)} />
      <Button size="sm" variant="secondary" onClick={() => setPicking(true)}><Images className="w-4 h-4 mr-1" />From library</Button>
      <Button size="sm" variant="secondary" onClick={() => setLinking(v => !v)}><Link2 className="w-4 h-4 mr-1" />Import from a link</Button>
      <Button size="sm" variant="primary" loading={uploading > 0} onClick={() => fileRef.current?.click()}>
        <UploadCloud className="w-4 h-4 mr-1" />{uploading > 0 ? `Uploading ${uploading}…` : 'Upload'}
      </Button>
    </>
  );

  const header = {
    onboarding: { eyebrow: 'Optional · makes your ads better', title: 'Your product photos and ads',
      description: 'Add photos of what you sell, and any finished ads or videos you already use. Admeru builds ads that show your real product, and can run your own ads as they are. Everything is saved in your Media Library.' },
    summary: { eyebrow: 'Your business', title: 'Products & media',
      description: 'The photos, ads and videos your strategy and plans can use. They live in your Media Library; tag them there.' },
    library: { eyebrow: 'Media Library', title: 'For planning',
      description: 'Files tagged for your strategy and plans: what each is, the products it shows and whether it can run as-is.' },
  }[mode];

  return (
    <Panel>
      <SectionHeader eyebrow={header.eyebrow} title={header.title} description={header.description} actions={actions} />

      {linking && <LinkImport onDone={next => { apply(next); setLinking(false); }} onClose={() => setLinking(false)} />}

      {summary && state.items.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <Chip tone="accent">{state.items.length} ready for planning</Chip>
          {state.kinds.filter(k => counts[k.id]).map(k => <Chip key={k.id}>{counts[k.id]} {k.label.toLowerCase()}{counts[k.id] === 1 ? '' : 's'}</Chip>)}
        </div>
      )}

      {mode === 'library' && state.items.length > 0 && (
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filter by type">
          <FilterChip on={filter === 'all'} onClick={() => setFilter('all')}>All · {state.items.length}</FilterChip>
          {state.kinds.filter(k => counts[k.id]).map(k => (
            <FilterChip key={k.id} on={filter === k.id} onClick={() => setFilter(k.id)}>{k.label} · {counts[k.id]}</FilterChip>
          ))}
        </div>
      )}

      {state.items.length === 0 ? (
        <button type="button" onClick={() => fileRef.current?.click()}
          className="w-full rounded-xl border-2 border-dashed border-border px-6 py-10 text-center hover:border-primary/60 hover:bg-primary-soft/40 transition-colors">
          <UploadCloud className="mx-auto mb-2 h-8 w-8 text-primary" />
          <p className="font-display text-base font-bold text-ink">Add your first product photo</p>
          <p className="mt-1 text-sm text-ink-2">Clear photos of each product work best. You can also add finished ads and videos.</p>
        </button>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-3">
          {(compact ? shown.slice(-6) : summary ? shown.slice(-8).reverse() : shown).map(it => (
            <Tile key={it.id} item={it} productName={productName} selected={it.id === openId}
              onClick={() => summary ? navigate(PLANNING_VIEW) : setOpenId(id => (id === it.id ? null : it.id))} />
          ))}
        </div>
      )}

      {open && !summary && <ItemEditor item={open} state={state} onClose={() => setOpenId(null)} onChanged={apply} />}

      {picking && <LibraryPicker onClose={() => setPicking(false)} onAdded={next => { apply(next); }} />}
    </Panel>
  );
};

const FilterChip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
  <button type="button" role="tab" aria-selected={on} onClick={onClick}
    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${on ? 'bg-primary text-white' : 'bg-surface text-ink-2 hover:text-ink'}`}>
    {children}
  </button>
);

export const Thumb: React.FC<{ url: string; video?: boolean; className?: string }> = ({ url, video, className = '' }) => (
  <div className={`relative overflow-hidden bg-surface ${className}`}>
    {video ? (
      <>
        <video src={url} className="h-full w-full object-cover" muted preload="metadata" />
        <span className="absolute inset-0 grid place-items-center"><span className="rounded-full bg-black/60 p-2"><Play className="h-4 w-4 text-white" /></span></span>
      </>
    ) : <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />}
  </div>
);

const Tile: React.FC<{ item: MediaItem; productName: (k: string) => string; selected: boolean; onClick: () => void }> = ({ item, productName, selected, onClick }) => (
  <button type="button" onClick={onClick} aria-pressed={selected}
    className={`group text-left rounded-xl border bg-card overflow-hidden transition-shadow ${selected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:shadow-md'}`}>
    <div className="relative">
      <Thumb url={item.url} video={item.media_type === 'video'} className="aspect-square" />
      <span className="absolute left-2 top-2 rounded-md bg-[#0F1624]/75 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
        {item.kind_label}{item.format ? ` · ${item.format}` : ''}
      </span>
      {item.use_as_is && <span className="absolute right-2 top-2 rounded-md bg-success px-1.5 py-0.5 text-[10px] font-semibold text-white">Runs as-is</span>}
    </div>
    <div className="p-2.5 space-y-1">
      <p className="truncate text-sm font-semibold text-ink">{item.label || 'Untitled'}</p>
      <p className="truncate text-xs text-ink-3">
        {item.offering_keys.length ? item.offering_keys.map(productName).join(', ') : 'Tag the product it shows'}
      </p>
    </div>
  </button>
);

export const ItemEditor: React.FC<{ item: MediaItem; state: MediaState; onClose: () => void; onChanged: (s: MediaState) => void; bare?: boolean }> = ({ item, state, onClose, onChanged, bare }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [label, setLabel] = useState(item.label);
  useEffect(() => { setLabel(item.label); }, [item.id, item.label]);

  const save = async (key: string, fields: Parameters<typeof brandKnowledgeApi.changeMedia>[1]) => {
    setBusy(key);
    try { onChanged(await brandKnowledgeApi.changeMedia(item.id, fields)); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(null); }
  };
  const toggleProduct = (k: string) => save('products', {
    offering_keys: item.offering_keys.includes(k) ? item.offering_keys.filter(x => x !== k) : [...item.offering_keys, k],
  });
  const remove = async () => {
    setBusy('remove');
    try { onChanged(await brandKnowledgeApi.removeMedia(item.id)); onClose(); }
    catch (e) { toast.error(errText(e)); setBusy(null); }
  };

  return (
    <div className={bare ? 'space-y-3' : 'rounded-xl border border-border bg-surface/50 p-4 grid gap-4 sm:grid-cols-[10rem_1fr]'}>
      {!bare && <Thumb url={item.url} video={item.media_type === 'video'} className="aspect-square rounded-lg" />}
      <div className="min-w-0 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 min-w-0 flex-1">
            <label htmlFor={`media-label-${item.id}`} className="text-small text-ink-2">Name</label>
            <input id={`media-label-${item.id}`} value={label} onChange={e => setLabel(e.target.value)}
              onBlur={() => label !== item.label && save('label', { label })}
              className="min-h-[44px] w-full rounded-btn border-0 bg-ground px-3.5 text-sm text-ink shadow-ring-line focus:outline-none focus:shadow-ring-ink" />
          </div>
          {!bare && <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-ink-3 hover:text-ink"><X className="h-4 w-4" /></button>}
        </div>
        <div className="space-y-1.5">
          <p className="m-0 text-small text-ink-2">What it is</p>
          <div className="flex flex-wrap gap-1.5">
            {state.kinds.map(k => (
              <button key={k.id} type="button" disabled={busy !== null} onClick={() => k.id !== item.kind && save('kind', { kind: k.id })}
                className={`min-h-[40px] rounded-full px-3.5 text-sm font-semibold transition-colors ${k.id === item.kind ? 'bg-navy text-on-navy' : 'bg-inset text-ink hover:bg-line'}`}>
                {k.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="m-0 text-small text-ink-2">Shows</p>
          {state.products.length === 0 ? (
            <p className="text-xs text-ink-3">Add your products in Your Strategy first, then tag them here.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {state.products.map(p => {
                const on = item.offering_keys.includes(p.key);
                return (
                  <button key={p.key} type="button" disabled={busy !== null} onClick={() => toggleProduct(p.key)}
                    className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-3.5 text-sm font-semibold transition-colors ${on ? 'bg-navy text-on-navy' : 'bg-inset text-ink hover:bg-line'}`}>
                    {on && <Check className="h-3 w-3" />}{p.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {RUNNABLE.includes(item.kind) && (
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" className="h-[18px] w-[18px] accent-[rgb(var(--c-ink-1))]" checked={item.use_as_is} disabled={busy !== null}
              onChange={e => save('use_as_is', { use_as_is: e.target.checked })} />
            Can run as-is in ads
            <span className="text-xs text-ink-3">(Admeru uses it unchanged)</span>
          </label>
        )}
        <div className="flex justify-end">
          <Button size="xs" variant="ghost" loading={busy === 'remove'} onClick={remove}><Trash2 className="h-3.5 w-3.5 mr-1" />Stop using in planning</Button>
        </div>
      </div>
    </div>
  );
};

export const LibraryPicker: React.FC<{ onClose: () => void; onAdded: (s: MediaState) => void }> = ({ onClose, onAdded }) => {
  const { toast } = useToast();
  const [items, setItems] = useState<MediaCandidate[] | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    brandKnowledgeApi.getMediaCandidates().then(setItems).catch(e => { toast.error(errText(e)); setItems([]); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    setBusy(true);
    let last: MediaState | null = null;
    for (const c of (items || []).filter(i => chosen.has(i.media_asset_id))) {
      try { last = await brandKnowledgeApi.addMedia(c.media_asset_id, { kind: c.suggested_kind }); }
      catch (e) { toast.error(errText(e)); }
    }
    if (last) { onAdded(last); toast.success(`Added ${chosen.size}. Tag the products they show.`); }
    setBusy(false);
    onClose();
  };

  return (
    <div className="rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-display font-bold text-ink">Add from your media library</p>
          <p className="text-xs text-ink-3">Your uploads and the ads you saved in Ad Studio.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-ink-3 hover:text-ink"><X className="h-4 w-4" /></button>
      </div>
      {!items ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        : items.length === 0 ? <p className="text-sm text-ink-2">Nothing new in your library. Upload files instead.</p>
        : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(6.5rem,1fr))] gap-2 max-h-80 overflow-y-auto pr-1">
            {items.map(c => {
              const on = chosen.has(c.media_asset_id);
              return (
                <button key={c.media_asset_id} type="button" aria-pressed={on}
                  onClick={() => setChosen(s => { const n = new Set(s); on ? n.delete(c.media_asset_id) : n.add(c.media_asset_id); return n; })}
                  className={`relative rounded-lg overflow-hidden border-2 ${on ? 'border-primary' : 'border-transparent'}`}>
                  <Thumb url={c.url} video={c.media_type === 'video'} className="aspect-square" />
                  {on && <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5"><Check className="h-3 w-3 text-white" /></span>}
                  <Chip className="absolute left-1 bottom-1 !bg-black/60 !text-white !text-[10px]">{c.source === 'ad_studio' ? 'Ad Studio' : c.format || c.media_type}</Chip>
                </button>
              );
            })}
          </div>
        )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button size="sm" variant="primary" disabled={!chosen.size} loading={busy} onClick={add}><Plus className="w-4 h-4 mr-1" />Add {chosen.size || ''}</Button>
      </div>
    </div>
  );
};

export const LinkImport: React.FC<{ onDone: (s: MediaState) => void; onClose: () => void }> = ({ onDone, onClose }) => {
  const { toast } = useToast();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { onDone(await brandKnowledgeApi.importMediaUrl(url.trim())); toast.success('Imported. Tag the product it shows.'); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap gap-2 rounded-xl border border-border p-3">
      <input value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && url.trim() && go()}
        placeholder="https://… link to an image (product page images work too)" aria-label="Image link"
        className="min-w-0 flex-1 rounded-lg border border-border bg-input px-3 py-1.5 text-sm text-ink" />
      <Button size="sm" variant="primary" loading={busy} disabled={url.trim().length < 8} onClick={go}>Import</Button>
      <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
    </div>
  );
};

/** The Media Library's file dialog: tag a file for planning, or start using it. */
export const PlanningTags: React.FC<{ assetId: string; mediaType: string; state: MediaState | null; onChanged: (s: MediaState) => void }> = ({ assetId, mediaType, state, onChanged }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  if (!state || (mediaType !== 'image' && mediaType !== 'video')) return null;
  const item = state.items.find(i => i.media_asset_id === assetId);
  return (
    <div className="mt-5 space-y-3 border-t-[1.5px] border-ink pt-4">
      <div>
        <p className="m-0 font-display text-card-title font-bold text-ink">Use in planning</p>
        <p className="m-0 text-small text-ink-2">Your strategy and monthly plans can use tagged files in your campaigns.</p>
      </div>
      {item ? <ItemEditor item={item} state={state} onClose={() => undefined} onChanged={onChanged} bare /> : (
        <Button size="sm" variant="primary" loading={busy} onClick={async () => {
          setBusy(true);
          try { onChanged(await brandKnowledgeApi.addMedia(assetId)); }
          catch (e) { toast.error(errText(e)); }
          finally { setBusy(false); }
        }}><Plus className="w-4 h-4 mr-1" />Use in planning</Button>
      )}
    </div>
  );
};
